/**
 * Whether the dietitian's own profile has been filled in — the question the
 * strip over the General tab asks, and the one that decides whether that tab
 * opens on the profile or on the form that edits it.
 *
 * The measure is the required fields and nothing else: a name to head the
 * profile with and a number to reach them on, which is all this screen insists
 * on — see `validation`. Everything beside them is a person saying more about
 * themselves, and a profile is not unfinished for leaving it unsaid.
 *
 * A field counts once it holds anything at all, and this deliberately does not
 * ask `fieldError` whether what it holds is acceptable. Validity is the Save
 * button's question, and it is answered where it can be acted on — against the
 * field, in words. The strip cannot say which field it is withholding credit
 * from, so a strict test would leave it standing over a form its owner can see
 * nothing wrong with. The brand's own `setupProgress` makes the same bargain,
 * and for the same reason: the backend normalises as it stores, so a stored
 * value is not always one the form's rules would accept back.
 *
 * A pure function of the profile in hand, so nothing is asked of the backend
 * for it.
 */

import type { DietitianProfile } from "./types/settings.types";
import { REQUIRED_FIELDS } from "./validation";

export const isProfileSetUp = (profile: DietitianProfile): boolean =>
  // Trimmed, so a field holding nothing but spaces is the empty field it looks
  // like rather than a step taken.
  REQUIRED_FIELDS.every((field) => profile[field].trim() !== "");

export default isProfileSetUp;
